import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {dialog, registerEvent} from '../../electron-modules';
import {store} from '../../redux/store';
import {
  AssetType,
  getCameraFileAssets,
  getModelAssets,
  getPointCloudAssets,
  selectAllAssets
} from '../../redux/assets-slice';
import {trackToolUsage} from '../../executable';
import {useSelector} from 'react-redux';
import {
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Theme,
  Typography,
  useTheme
} from '@material-ui/core';
import {
  AlertDialog,
  CheckboxWithLabel,
  FolderWithLabel,
  LinearProgressWithLabel,
  SimpleDialog,
} from '../../components';
import sanitize from "sanitize-filename";
import {selectProjectName} from '../../redux/project-slice';
import {toast} from '../../app';
import {useTranslation} from 'react-i18next';
import {getStaticExporter} from "./exporter";
import {useDialog} from "../../hooks";
import {getLocalizedProjectName} from '../../utilities';
import {throttle} from 'throttle-debounce';
import { NewProjectInfo } from '../common';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    warning: {
      color: theme.palette.warning.main
    },
    exportText: {
      padding: theme.spacing(1)
    },
    contentCenter: {
      textAlign: "center"
    },
    cancelProgress: {
      marginRight: theme.spacing(1)
    },
    cancelContent: {
      paddingBottom: theme.spacing(3)
    },
    paddingTop: {
      paddingTop: theme.spacing(3)
    },
    listItem: {
      width: '100%',
      marginTop: theme.spacing(2)
    },
    switchLabel: {
      display: "inline-block",
      maxWidth: "calc(100% - 50px)"
    },
    switchElement: {
      float: "right"
    },
    outputFolder: {
      marginTop: theme.spacing(2)
    },
    dropdown: {
      width: "40%"
    },
    header: {
      marginTop: theme.spacing(1)
    }
  }),
);

export const StaticExporter = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const exporterDialog = useDialog();
  const messageDialog = useDialog();
  const successDialog = useDialog();
  const cancelDialog = useDialog();

  const projectName = useSelector(selectProjectName);
  const assets = useSelector(selectAllAssets);
  const pointClouds = getPointCloudAssets(assets);
  const camerasFiles = getCameraFileAssets(assets);
  const modelFiles = getModelAssets(assets);

  const [outputPath, setOutputPath] = useState(null);
  const [singleFileOutput, setSingleFileOutput] = useState(true);
  const [exportTopMessage, setExportTopMessage] = useState("");
  const [exportBottomMessage, setExportBottomMessage] = useState("");
  const [exportPercent, updateExportPercent] = useState(0);
  const [isExportRunning, setIsExportRunning] = useState(false);

  const [startExport, cancelExport] = useMemo(getStaticExporter, []);

  const progressThrottleMilliseconds = 100;

  const setExportPercent = useCallback(throttle(
    progressThrottleMilliseconds, (value) => {
      updateExportPercent(value);
    }), []);

  const canExport = assets.length > 0;

  const numRawPointCloud = pointClouds.filter(asset => {
    return (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points);
  }).length;

  const onExportFinished = () => {
    setIsExportRunning(false);
    messageDialog.handleClose();
  };

  const handleExportCancel = () => {
    onExportFinished();
    cancelDialog.handleOpen();
    cancelExport();
  };

  const handleExport = () => {
    exporterDialog.handleClose();
    exportToStatic();
  };

  const setExportMessage = (top = "", bottom = "") => {
    setExportTopMessage(top);
    setExportBottomMessage(bottom);
  };

  const exportToStatic = async () => {
    trackToolUsage("exporter");

    setExportPercent(0);
    setExportMessage();
    messageDialog.handleOpen();

    const assets = [
      ...pointClouds,
      ...camerasFiles,
      ...modelFiles
    ];

    await startExport({
      name: exportName,
      outputPath: outputPath,
      assets: assets,
      singleFileOutput: singleFileOutput,
      state: store.getState(),
      onUpdate: setExportMessage,
      onLogs: setExportBottomMessage,
      onProgress: setExportPercent,
      onError: (title, message) => {
        onExportFinished();
        dialog.showErrorBox(title, message);
      },
      onFinish: () => {
        onExportFinished();
        successDialog.handleOpen();
      },
      onCancel: () => {
        setIsExportRunning(false);
        cancelDialog.handleClose();
      }
    });
  };

  const resetDefaults = () => {
    setOutputPath(null);
    setSingleFileOutput(true);
  };

  useEffect(() => {
    registerEvent("open-static-export", () => {
      if (!canExport) return toast.error(t("toast.no-data-loaded"));
      exporterDialog.handleOpen();
    });
  }, [canExport]);

  useEffect(() => {
    if (!exporterDialog.open) return;
    resetDefaults();
  }, [exporterDialog.open]);

  const localizedProjectName = getLocalizedProjectName(projectName);
  const sanitizedName = sanitize(localizedProjectName);
  const exportName = t('export.export-name', {name: sanitizedName});

  return (
    <React.Fragment>
      {/* Exporting dialog */}
      <Dialog
        open={messageDialog.open}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('export.export-status')}</DialogTitle>

        <DialogContent className={classes.contentCenter}>
          <Typography variant="body1" className={classes.exportText}>
            {exportTopMessage}
          </Typography>

          <Typography variant="body2" className={classes.exportText}>
            {exportBottomMessage}
          </Typography>

          <LinearProgressWithLabel value={exportPercent} color="secondary"/>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleExportCancel}
            color="primary"
            disabled={isExportRunning}
          >
            {t('export.cancel-export')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main dialog */}
      <SimpleDialog
        open={exporterDialog.open}
        onClose={exporterDialog.handleClose}
        onSubmit={handleExport}
        canSubmit={outputPath}
        maxWidth={"xs"}
        title={t('export.data-export')}
        button={t("buttons.export")}
      >
        <Grid container spacing={2}>

          {/* Export file type(s) info */}
          <Grid item xs={12}>
            <Typography
              dangerouslySetInnerHTML={{
                __html: t("export.folder-text", {
                  name: exportName
                })
              }}
            />

            <NewProjectInfo/>
          </Grid>

          {/* Point cloud export version */}
          {(numRawPointCloud > 0) && (
            <Grid item xs={12}>
              <CheckboxWithLabel
                title={t('export.streamable_cloud_version_2_0')}
                label={t('export.the_newest_version_of_the_streamable_point_cloud')}
                value={singleFileOutput}
                onChange={setSingleFileOutput}
              />
            </Grid>
          )}

          {/* Output Folder */}
          <Grid item xs={12}>
            <FolderWithLabel
              title={t('export.output_folder')}
              label={t('export.no-folder-selected')}
              value={outputPath}
              onChange={setOutputPath}
            />
          </Grid>

        </Grid>
      </SimpleDialog>

      {/* Cancelling dialog */}
      <Dialog
        open={cancelDialog.open}
      >
        <DialogTitle>{t('export.data-export')}</DialogTitle>
        <DialogContent className={classes.cancelContent}>
          <CircularProgress size="1em" className={classes.cancelProgress}/>
          <DialogContentText display="inline">
            {t('export.cancelling-export')}
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Success dialog */}
      <AlertDialog
        title={t('export.data-export')}
        open={successDialog.open}
        onClose={successDialog.handleClose}
      >
        <DialogContentText>
          {t('export.successfully-exported-data')}
        </DialogContentText>
      </AlertDialog>
    </React.Fragment>
  );
};